import React, { useState, useEffect } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement } from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';
import { placementAPI, apiUtils } from '../../utils/api';
import './Analytics.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement);

const Analytics = ({ filters }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadAnalytics();
  }, [filters.season]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const data = await placementAPI.getAnalytics(filters.season);
      setAnalytics(data);
    } catch (error) {
      const errorInfo = apiUtils.handleError(error);
      setError(errorInfo.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="analytics-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <div className="loading-text">Loading analytics...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-container">
        <div className="error-container">
          <i className="fas fa-exclamation-triangle error-icon"></i>
          <div className="error-message">Failed to load analytics</div>
          <div className="error-description">{error}</div>
          <button className="btn btn-primary mt-3" onClick={loadAnalytics}>
            <i className="fas fa-refresh me-2"></i>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const statusChartData = {
    labels: Object.keys(analytics.statusCounts),
    datasets: [{
      label: 'Applications',
      data: Object.values(analytics.statusCounts),
      backgroundColor: [
        '#667eea', '#dc3545', '#fd7e14', '#6f42c1',
        '#ffc107', '#28a745', '#20c997', '#6c757d'
      ],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  const companyChartData = {
    labels: analytics.companyStats.slice(0, 10).map(stat => stat.company_name),
    datasets: [{
      label: 'Applications',
      data: analytics.companyStats.slice(0, 10).map(stat => stat.application_count),
      backgroundColor: '#667eea',
      borderColor: '#5a67d8',
      borderWidth: 1
    }]
  };

  const timelineChartData = {
    labels: analytics.timelineData.map(item => new Date(item.application_date).toLocaleDateString()),
    datasets: [{
      label: 'Applications per Day',
      data: analytics.timelineData.map(item => item.count),
      borderColor: '#667eea',
      backgroundColor: 'rgba(102, 126, 234, 0.1)',
      tension: 0.4,
      fill: true
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
  };

  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <h3>Analytics Dashboard</h3>
        <p>Comprehensive insights into your placement applications</p>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="card-icon">
            <i className="fas fa-paper-plane"></i>
          </div>
          <div className="card-content">
            <h4>{analytics.summary.totalApplications}</h4>
            <p>Total Applications</p>
          </div>
        </div>

        <div className="summary-card success">
          <div className="card-icon">
            <i className="fas fa-check-circle"></i>
          </div>
          <div className="card-content">
            <h4>{analytics.summary.successfulApplications}</h4>
            <p>Successful Applications</p>
          </div>
        </div>

        <div className="summary-card info">
          <div className="card-icon">
            <i className="fas fa-percentage"></i>
          </div>
          <div className="card-content">
            <h4>{analytics.summary.successRate}%</h4>
            <p>Success Rate</p>
          </div>
        </div>

        <div className="summary-card warning">
          <div className="card-icon">
            <i className="fas fa-star"></i>
          </div>
          <div className="card-content">
            <h4>{analytics.summary.averagePriority || 'N/A'}</h4>
            <p>Avg Priority Rating</p>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="charts-grid">
        <div className="chart-card">
          <h5>Application Status Distribution</h5>
          <div className="chart-container">
            <Pie data={statusChartData} options={chartOptions} />
          </div>
        </div>

        <div className="chart-card">
          <h5>Top 10 Companies by Applications</h5>
          <div className="chart-container">
            <Bar data={companyChartData} options={chartOptions} />
          </div>
        </div>

        <div className="chart-card full-width">
          <h5>Application Timeline</h5>
          <div className="chart-container">
            <Line data={timelineChartData} options={chartOptions} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
